import React, { useEffect, useCallback } from 'react';
import { GetServerSideProps } from 'next';
import HeadContent from '../../../../components/HeadContent';
import BasePage, {
  SchedulerData,
  BasePageProps,
} from '../../../../components/BasePage';
import { getBasePageProps } from '../../../../lib/basePageProps';
import {
  errorPageViewEvent,
  referralPageViewEvent,
  exposureEvent,
  benefitImpressionEvent,
} from '../../../../lib/tracking/eventPayloadDefinitions';
import { trackEvent } from '../../../../lib/tracking';
import {
  EventParams,
  setCheckoutButtonHref,
  addCheckoutButtonTracking,
} from '../../../../lib/checkoutButton';
import { extractUserIdNoError } from '../../../../server/lib/userid';
import { getUserIdFromReferralCode } from '../../../../server/api/partner-userid';
import { getScheduledResource } from '../../../../server/api/scheduler';
import { getShareCardUrl } from '../../../../lib/thestage-utils';
import { getMarketConfig } from '../../../../server/api/country-attributes/country-attributes';
import { getUserCountry } from '../../../../server/api/useraccount';
import { resolveUseShareCardCdn } from '../../../../server/lib/remote-config';
// CountryTracking is the user's registration country (geolocation is the fallback)
// Market is the user's geolocation
export type PageProps = {
  basePageProps: BasePageProps;
  schedulerData: SchedulerData;
  market: string;
  countryTracking: string;
  code: string;
  isAuthenticated: boolean;
  errorCode?: string | null;
  senderUserId?: string;
  shareId?: string;
};

const RECEIVER_PAGE = 'referrals:receiver';

export const getServerSideProps: GetServerSideProps<
  PageProps,
  { market: string; code: string }
> = async ({ req, res, params, query }) => {
  let pageName = RECEIVER_PAGE;

  const userId = extractUserIdNoError(req);
  const marketGeolocation = params!.market;
  let registrationCountry = null;

  if (userId) {
    registrationCountry = await getUserCountry(userId);
  }

  const {
    country: marketCountry,
    system_language: locale,
    translation_locales,
  } = await getMarketConfig({
    spotifyMarket: marketGeolocation,
  });

  const shareId = typeof query.si === 'string' ? query.si : undefined;

  const [basePageData, schedulerResponse, senderUserIdData] =
    await Promise.allSettled([
      getBasePageProps({
        spotifyMarket: marketGeolocation,
        userId: extractUserIdNoError(req),
      }),
      getScheduledResource({
        localisation: {
          translationLocales: translation_locales,
          currencyLocale: locale,
          dateLocale: locale,
        },
        platform: 'WEB',
        resource: pageName,
        targeting: {
          country: registrationCountry ? registrationCountry : marketCountry,
        },
      }),
      getUserIdFromReferralCode({ code: params!.code }),
    ]);

  let schedulerData;

  if (basePageData.status === 'rejected') {
    res.statusCode = 500;
    throw basePageData.reason;
  }

  const sanitizedBasePageProps = JSON.parse(
    JSON.stringify(basePageData.value, (key, value) => {
      if (key === 'scrollingElementAccessor') {
        return undefined;
      }
      return value;
    }),
  );

  if (schedulerResponse.status === 'rejected') {
    try {
      pageName = 'referrals:error:country-invalid';

      schedulerData = await getScheduledResource({
        localisation: {
          translationLocales: translation_locales,
          currencyLocale: locale,
          dateLocale: locale,
        },
        platform: 'WEB',
        resource: pageName,
        targeting: {
          country: registrationCountry ? registrationCountry : marketCountry,
        },
      });

      schedulerData = schedulerData.content;
    } catch (e) {
      res.statusCode = 500;
      throw e;
    }
  } else {
    schedulerData = schedulerResponse.value.content;

    const resolvedLocale =
      schedulerResponse.value.metadata?.resolvedTranslationLocale;
    const eventParams = schedulerData?.components?.find(
      (component: any) => component.eventParams?.offerTypeId,
    )?.eventParams;

    if (eventParams?.offerTypeId) {
      try {
        let useCdn = false;
        if (senderUserIdData.status === 'fulfilled') {
          try {
            useCdn = await resolveUseShareCardCdn(req, senderUserIdData.value);
          } catch {
            // Fall back to old CDN on resolution failure
          }
        }
        schedulerData.metadata.image = await getShareCardUrl(
          resolvedLocale || locale,
          eventParams.offerTypeId,
          marketCountry,
          useCdn,
          eventParams.campaignId,
        );
      } catch {
        // Keep static scheduler image as fallback
      }
    }
  }

  const isAuthenticated = !!req.headers['sp-internal-user-id'];

  const errorCode = pageName === RECEIVER_PAGE ? null : pageName;

  const senderUserId =
    senderUserIdData.status === 'fulfilled'
      ? senderUserIdData.value
      : undefined;

  const country = registrationCountry ? registrationCountry : marketCountry;

  return {
    props: {
      basePageProps: sanitizedBasePageProps,
      schedulerData: schedulerData,
      market: marketGeolocation,
      countryTracking: country,
      code: params!.code,
      isAuthenticated,
      errorCode,
      ...(senderUserId && { senderUserId }),
      ...(shareId && { shareId }),
    },
  };
};

const ReferralPage = (props: PageProps) => {
  const {
    basePageProps,
    schedulerData,
    market,
    countryTracking,
    code,
    isAuthenticated,
    errorCode,
    senderUserId,
    shareId,
  } = props ?? ({} as Partial<PageProps>);

  useEffect(() => {
    if (!schedulerData) return;

    trackEvent(
      exposureEvent(
        {
          variant: 'v1',
          country: countryTracking,
          context: errorCode ?? 'referrals-receiver',
        },
        { authorize: isAuthenticated },
      ),
    );

    if (errorCode) {
      trackEvent(
        errorPageViewEvent(
          {
            page: errorCode,
            market: countryTracking,
            sender_user_id: senderUserId,
            share_id: shareId,
          },
          { authorize: isAuthenticated },
        ),
      );
    } else {
      trackEvent(
        referralPageViewEvent(
          {
            page: 'referrals:receiver',
            market: countryTracking,
            http_referer: document.referrer,
            sender_user_id: senderUserId,
            share_id: shareId,
          },
          { authorize: isAuthenticated },
        ),
      );
    }
  }, [
    schedulerData,
    countryTracking,
    errorCode,
    isAuthenticated,
    senderUserId,
    shareId,
  ]);

  const handleBenefitImpression = useCallback(
    (benefit: string, position: number) => {
      trackEvent(
        benefitImpressionEvent(
          {
            page: 'referrals:receiver:v1',
            market: countryTracking,
            component: benefit,
            position,
            variant: 'v1',
          },
          { authorize: isAuthenticated },
        ),
      );
    },
    [countryTracking, isAuthenticated],
  );

  if (!schedulerData) return null;

  const enrichSchedulerData = (simpleSchedulerData: SchedulerData) => {
    const makeCheckoutLink = (eventParams: EventParams) =>
      `/${market}/referral/${code}/continue/${eventParams.campaignId}-${eventParams.offerTypeId}`;

    const [enrichedSchedulerData] = [simpleSchedulerData]
      .map(data => setCheckoutButtonHref(data, makeCheckoutLink))
      .map(data =>
        addCheckoutButtonTracking(
          data,
          countryTracking,
          code,
          { authorize: isAuthenticated },
          { variant: 'v1', country: countryTracking },
        ),
      );

    return enrichedSchedulerData;
  };

  return (
    <>
      <HeadContent {...schedulerData.metadata} />
      <BasePage
        basePageProps={basePageProps}
        schedulerData={
          errorCode ? schedulerData : enrichSchedulerData(schedulerData)
        }
        onBenefitImpression={errorCode ? undefined : handleBenefitImpression}
      />
    </>
  );
};

export default ReferralPage;
